/**
 * Legal documents are created and published in the admin panel and served from
 * `/public/api/v1/legal`. Which documents exist, what they are called and whether
 * they are shown are all admin data — the only thing named here is the handful the
 * app itself has behaviour for.
 */

/** Every key the app refers to by name. Which documents exist is still admin data. */
export enum LegalDocumentKey {
  TERMS_OF_SERVICE = 'terms-of-service',
  PRIVACY_POLICY = 'privacy-policy',
  DIGITAL_ASSETS_DISCLOSURE = 'digital-assets-disclosure',
}

/**
 * Documents onboarding makes the user accept. Mirrors the backend's `ConsentDocumentKey`;
 * requiring a second document is one line in each. Listed, not derived from the enum, so
 * naming a key for a link can never turn it into something the user is forced to accept.
 */
export const CONSENT_DOCUMENT_KEYS = [
  LegalDocumentKey.TERMS_OF_SERVICE,
] as const;

/**
 * The documents the signup line names, in this order. Fixed rather than read from the live
 * list: publishing a fourth document must never silently lengthen the sentence a user agrees
 * to. A key absent from the list is skipped, so hiding one in the admin still takes effect.
 */
export const SIGNUP_LEGAL_KEYS = [
  LegalDocumentKey.TERMS_OF_SERVICE,
  LegalDocumentKey.PRIVACY_POLICY,
  LegalDocumentKey.DIGITAL_ASSETS_DISCLOSURE,
] as const;
/**
 * Two urls Google already has on file, registered in the Play Console (AML and
 * "Safety standards"). They must resolve to real content on every deploy, whatever
 * the admin panel currently holds — a broken Safety standards url puts the Play
 * listing at risk — so each keeps its own top-level path *and* a built-in page that
 * renders whenever the admin document cannot be shown.
 */
export const LEGACY_LEGAL_PATHS = ['aml-policy', 'child-safety'] as const;

export type LegacyLegalPath = (typeof LEGACY_LEGAL_PATHS)[number];

export function isLegacyLegalPath(
  key: string | undefined,
): key is LegacyLegalPath {
  return (LEGACY_LEGAL_PATHS as readonly string[]).includes(key ?? '');
}

/**
 * `terms-of-use` -> `/legal/terms-of-use`. The key is the slug the admin panel generated.
 *
 * The two legacy documents keep their own top-level path instead: it is the url Google
 * has on file and the only one with a built-in fallback, so every link must point there
 * rather than creating a second, more fragile address for the same text.
 */
export function legalDocumentPath(key: string): string {
  return isLegacyLegalPath(key) ? `/${key}` : `/legal/${key}`;
}

/**
 * Where the legal links point until the admin documents are live.
 *
 * These are the urls that shipped before this feature. They stay in the code on
 * purpose: publishing our policies and keeping them reachable is an intermediary
 * obligation, so a footer with no Terms and no Privacy Policy link is not an
 * acceptable in-between state — not while the flag is off, and not if the document
 * list ever comes back empty.
 *
 * `labelKey`/`useCommon`/`external` match the shape the footer already renders.
 */
export const FALLBACK_LEGAL_LINKS = [
  {
    key: LegalDocumentKey.PRIVACY_POLICY,
    labelKey: 'pages.privacyPolicy',
    href: 'https://www.notion.so/Espotz-Privacy-Policy-2a9395f1893180e599e9ca9c7f39d606',
    external: true,
    useCommon: true,
  },
  {
    key: LegalDocumentKey.TERMS_OF_SERVICE,
    labelKey: 'pages.termsOfService',
    href: 'https://www.notion.so/Espotz-live-Terms-of-Use-2a9395f189318076903de6a50273706a',
    external: true,
    useCommon: true,
  },
  {
    key: LegalDocumentKey.DIGITAL_ASSETS_DISCLOSURE,
    labelKey: 'pages.digitalAssetsDisclosure',
    href: 'https://www.notion.so/Espotz-live-Cryptocurrency-Risk-Disclosure-2a9395f18931809aaaa5ccd9586e27ce',
    external: true,
    useCommon: true,
  },
  // Both render from the built-in page while the flag is off, so they stay internal.
  {
    key: 'aml-policy',
    labelKey: 'pages.amlPolicy',
    href: '/aml-policy',
    useCommon: true,
  },
  {
    key: 'child-safety',
    labelKey: 'pages.childSafety',
    href: '/child-safety',
    useCommon: true,
  },
] as const;
